import React from "react";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {
  useGetLSPSStateQuery,
  useGetMicroServiceMonitoringQuery,
  useGetTargetDataNowQuery,
  useGetMicroServiceMonitoringForcastQuery,
  useGetTotalInstalledCapacityQuery,
} from "./operatorDashboardAPISlice";
import CircularProgress from "@mui/material/CircularProgress";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import Tooltip from "@mui/material/Tooltip";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

export default function OperatorDashboardDemand() {

  const { data: totalInstalledCapacity, isLoading: totalInstalledCapacityLoading } =
    useGetTotalInstalledCapacityQuery(null, { pollingInterval: 60000 });
  const { data: lspsState, isLoading: isLSPSStateLoading } =
    useGetLSPSStateQuery(null, { pollingInterval: 60000 });
  const { data: microserviceState, isLoading: isMicroServiceStateLoading } =
    useGetMicroServiceMonitoringQuery(null, { pollingInterval: 60000 });
  const { data: microserviceForcastState, isLoading: isMicroServiceForcastStateLoading } =
    useGetMicroServiceMonitoringForcastQuery(null, { pollingInterval: 60000 });
  const { data: targetDataNow, isLoading: isTargetDataNowLoading } =
    useGetTargetDataNowQuery(null, { pollingInterval: 60000 });

  // Shared styles
  const cardSx = {
    padding: "20px 18px",
    backgroundColor: "#161B22",
    border: "1px solid rgba(253,185,64,0.18)",
    borderRadius: "10px",
    minHeight: "96px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  };
  const labelSx = {
    fontSize: "0.78rem",
    fontWeight: 500,
    color: "rgba(255,255,255,0.65)",
    mb: "8px",
  };
  const valueSx = {
    fontSize: "1.8rem",
    fontWeight: 700,
    color: "#fff",
    fontVariantNumeric: "tabular-nums",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  };
  const unitSx = {
    fontSize: "0.82rem",
    color: "rgba(255,255,255,0.45)",
    fontWeight: 500,
  };

  const SectionHeading = ({ label, info, infoTitle }) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: "12px", mb: "10px", mt: "20px" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
        <Typography sx={{ fontSize: "0.68rem", fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.35)", whiteSpace: "nowrap" }}>
          {label}
        </Typography>
        {info && (
          <Tooltip title={infoTitle ? <><strong>{infoTitle}</strong><br />{info}</> : info} placement="top" arrow>
            <InfoOutlinedIcon sx={infoIconSx} />
          </Tooltip>
        )}
      </Box>
      <Box sx={{ width: "24px", height: "1px", bgcolor: "rgba(255,255,255,0.25)", flexShrink: 0 }} />
    </Box>
  );

  const smallCardSx = {
    padding: "14px 16px",
    backgroundColor: "#161B22",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: "10px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  };
  const smallValueSx = {
    fontSize: "1.35rem",
    fontWeight: 700,
    color: "#fff",
    fontVariantNumeric: "tabular-nums",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  };
  const smallLabelSx = {
    fontSize: "0.72rem",
    fontWeight: 500,
    color: "rgba(255,255,255,0.5)",
    mb: "6px",
  };

  const infoIconSx = {
    fontSize: "13px",
    color: "rgba(255,255,255,0.3)",
    cursor: "help",
    flexShrink: 0,
    "&:hover": { color: "rgba(255,255,255,0.65)" },
  };
  const grid4 = { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "12px", width: "100%" };
  const grid3 = { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "12px", width: "100%" };

  return (
    <Box sx={{ width: "100%" }}>

      {/* ── PERIOD INTEGRATED DEMAND FORECAST ── */}
      <SectionHeading label="Period Integrated Demand Forecast" />
      <Box sx={grid4}>

        {/* Current Demand */}
        <Paper elevation={0} sx={cardSx}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px", mb: "8px" }}>
            <Typography sx={{ ...labelSx, mb: 0 }}>Current Demand</Typography>
            <Tooltip title="Summated active power flowing through all bulk Eskom incomers feeding this distribution area, in MW on the demand curve." placement="top" arrow>
              <InfoOutlinedIcon sx={infoIconSx} />
            </Tooltip>
          </Box>
          {isMicroServiceStateLoading ? (
            <CircularProgress size={20} sx={{ color: "#fff" }} />
          ) : (
            <Typography sx={valueSx}>
              {microserviceState?.current_demand !== undefined
                ? (microserviceState.current_demand / 1000000).toFixed(3)
                : "No Demand"}
              <Box component="span" sx={unitSx}>MW</Box>
            </Typography>
          )}
        </Paper>

        {/* Integrated Demand */}
        <Paper elevation={0} sx={cardSx}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px", mb: "8px" }}>
            <Typography sx={{ ...labelSx, mb: 0 }}>Integrated Demand</Typography>
            <Tooltip title="The current integrated demand. (Resets to zero at the start of the metering period)" placement="top" arrow>
              <InfoOutlinedIcon sx={infoIconSx} />
            </Tooltip>
          </Box>
          {isMicroServiceStateLoading ? (
            <CircularProgress size={20} sx={{ color: "#fff" }} />
          ) : (
            <Typography sx={valueSx}>
              {microserviceState?.latest_integrated_demand !== undefined
                ? (microserviceState.latest_integrated_demand / 1000000).toFixed(3)
                : "No Demand"}
              <Box component="span" sx={unitSx}>MW</Box>
            </Typography>
          )}
        </Paper>

        {/* Tjommie Demand */}
        <Paper elevation={0} sx={cardSx}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px", mb: "8px" }}>
            <Typography sx={{ ...labelSx, mb: 0 }}>Tjommie Demand</Typography>
            <Tooltip title="The current geyser demand / load" placement="top" arrow>
              <InfoOutlinedIcon sx={infoIconSx} />
            </Tooltip>
          </Box>
          {isMicroServiceStateLoading ? (
            <CircularProgress size={20} sx={{ color: "#fff" }} />
          ) : (
            <Typography sx={valueSx}>
              {microserviceState?.geyser_demand !== undefined
                ? (microserviceState.geyser_demand / 1000000).toFixed(3)
                : "No Demand"}
              <Box component="span" sx={unitSx}>MW</Box>
            </Typography>
          )}
        </Paper>

        {/* Forecast */}
        <Paper elevation={0} sx={cardSx}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px", mb: "8px" }}>
            <Typography sx={{ ...labelSx, mb: 0 }}>Forecast</Typography>
            <Tooltip title="The forecast for the total demand at the end of the current metering period" placement="top" arrow>
              <InfoOutlinedIcon sx={infoIconSx} />
            </Tooltip>
          </Box>
          {isMicroServiceForcastStateLoading ? (
            <CircularProgress size={20} sx={{ color: "#fff" }} />
          ) : (
            <>
              <Typography sx={valueSx}>
                {microserviceForcastState?.[0]?.forecast !== undefined
                  ? (microserviceForcastState[0].forecast / 1000000).toFixed(3)
                  : "No Forecast"}
                <Box component="span" sx={unitSx}>MW</Box>
                {microserviceForcastState?.[0]?.forecast > microserviceForcastState?.[1]?.forecast ? (
                  <ArrowUpwardIcon sx={{ color: "#ff0000", fontSize: "1rem" }} />
                ) : (
                  <ArrowDownwardIcon sx={{ color: "#358B00", fontSize: "1rem" }} />
                )}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: "5px", mt: "3px" }}>
                <Box sx={{ width: "8px", height: "8px", borderRadius: "50%", bgcolor: "#DC6CFE", opacity: 0.6, flexShrink: 0 }} />
                <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.45)", fontWeight: 500 }}>
                  prev{" "}
                  {microserviceForcastState?.[1]?.forecast !== undefined
                    ? (microserviceForcastState[1].forecast / 1000000).toFixed(3)
                    : "—"}{" "}
                  MW
                </Typography>
              </Box>
            </>
          )}
        </Paper>

      </Box>

      {/* ── LIMITS & TARGETS ── */}
      <SectionHeading label="Limits & Targets" infoTitle="Active load-shift schedule" info="The target demand and maximum return settings currently active across the DSM Cluster" />
      <Box sx={grid3}>

        {/* Target Demand */}
        <Paper elevation={0} sx={smallCardSx}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px", mb: "6px" }}>
            <Typography sx={{ ...smallLabelSx, mb: 0 }}>Target Demand</Typography>
            <Tooltip title="The current target demand as initially set by the Operator for the month and updated by the system if exceeded" placement="top" arrow>
              <InfoOutlinedIcon sx={infoIconSx} />
            </Tooltip>
          </Box>
          {isTargetDataNowLoading ? (
            <CircularProgress size={20} sx={{ color: "#fff" }} />
          ) : (
            <Typography sx={smallValueSx}>
              {targetDataNow?.target !== undefined
                ? (targetDataNow.target / 1000000).toFixed(3)
                : "No Target"}
              <Box component="span" sx={unitSx}>MW</Box>
            </Typography>
          )}
        </Paper>

        {/* Total Geyser Installed Capacity */}
        <Paper elevation={0} sx={smallCardSx}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px", mb: "6px" }}>
            <Typography sx={{ ...smallLabelSx, mb: 0 }}>Total Geyser Installed Capacity</Typography>
            <Tooltip title="Sum of rated electrical capacity (MW) of all geysers enrolled in the programme." placement="top" arrow>
              <InfoOutlinedIcon sx={infoIconSx} />
            </Tooltip>
          </Box>
          {totalInstalledCapacityLoading ? (
            <CircularProgress size={20} sx={{ color: "#fff" }} />
          ) : (
            <Typography sx={smallValueSx}>
              {totalInstalledCapacity !== undefined ? totalInstalledCapacity.toFixed(3) : "No value"}
              <Box component="span" sx={unitSx}>MW</Box>
            </Typography>
          )}
        </Paper>

        {/* Max Return Load */}
        <Paper elevation={0} sx={smallCardSx}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px", mb: "6px" }}>
            <Typography sx={{ ...smallLabelSx, mb: 0 }}>Max Return Load</Typography>
            <Tooltip title="The maximum total demand allowed on the system" placement="top" arrow>
              <InfoOutlinedIcon sx={infoIconSx} />
            </Tooltip>
          </Box>
          {isLSPSStateLoading ? (
            <CircularProgress size={20} sx={{ color: "#fff" }} />
          ) : (
            <Typography sx={smallValueSx}>
              {lspsState?.max_return ?? "—"}
              <Box component="span" sx={unitSx}>MW</Box>
            </Typography>
          )}
        </Paper>

      </Box>
    </Box>
  );
}
